import React, { useEffect, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import StethoscopeIcon from "@mui/icons-material/MonitorHeart";
import EditIcon from "@mui/icons-material/Edit";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdLabelSx,
  IpdButton,
  IpdSectionCard,
  IpdTextField,
} from "../../ipd/utils";
import { formatDateTime } from "../constants";
import { saveAssessment } from "../api/emergencyApi";

/**
 * The clinical assessment (ER-ASM-001, ER-ASM-006).
 *
 * Four fields rather than one note, because they are read separately: a nurse
 * checking the plan should not have to find it inside a wall of history. They
 * are shown in the order a provider works — what they were told, what they
 * found, what they think, what they intend to do.
 *
 * Read mode by default once an assessment exists. This is the block other
 * staff come to the visit to read, and putting it behind a form would make the
 * most-read thing on the screen the least legible.
 */
const FIELDS = [
  {
    key: "history",
    label: "History",
    placeholder: "What the patient or the people with them describe…",
    rows: 3,
  },
  {
    key: "examination",
    label: "Examination findings",
    placeholder: "What the examination showed…",
    rows: 3,
  },
  {
    key: "working_diagnosis",
    label: "Working diagnosis",
    placeholder: "What this looks like for now — it can change…",
    rows: 2,
  },
  {
    key: "plan",
    label: "Plan",
    placeholder: "Intended interventions, investigations, review points…",
    rows: 3,
  },
];

const emptyDraft = () =>
  FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const draftFrom = (assessment) =>
  FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: assessment?.[f.key] || "" }), {});

export default function AssessmentCard({ visit, onSaved, disabled }) {
  const assessment = visit.assessment || null;

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Re-seed whenever the visit reloads, so an edit started after someone else
  // saved theirs begins from what is actually recorded rather than a stale copy.
  useEffect(() => {
    setDraft(draftFrom(assessment));
  }, [assessment]);

  const startEditing = () => {
    setDraft(draftFrom(assessment));
    setError("");
    setEditing(true);
  };

  const cancel = () => {
    setDraft(draftFrom(assessment));
    setError("");
    setEditing(false);
  };

  const anyContent = FIELDS.some((f) => draft[f.key].trim());

  const submit = async () => {
    if (!anyContent) return;
    setBusy(true);
    setError("");
    try {
      // Every field is sent, including the blanked ones — the server treats an
      // empty string as "clear this" and an omitted key as "leave it", and a
      // provider who deleted a paragraph means the former.
      const payload = FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: draft[f.key].trim() }),
        {}
      );
      await saveAssessment(visit.id, payload);
      await onSaved?.();
      setEditing(false);
    } catch (e) {
      setError(e?.message || "Could not save the assessment");
    } finally {
      setBusy(false);
    }
  };

  const byline = assessment && (
    <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted, mt: 1.5 }}>
      {assessment.was_edited
        ? `Last edited by ${assessment.updated_by_name || "unknown"} · ${formatDateTime(
            assessment.updated_at
          )}`
        : `Recorded by ${assessment.recorded_by_name || "unknown"} · ${formatDateTime(
            assessment.recorded_at
          )}`}
      {assessment.was_edited && assessment.recorded_by_name
        ? ` · first recorded by ${assessment.recorded_by_name}`
        : ""}
    </Typography>
  );

  /* ------------------------------------------------------------ read mode */
  if (!editing) {
    return (
      <IpdSectionCard
        title="Clinical assessment"
        subtitle="What the provider found, and what they intend to do"
        actions={
          !disabled && (
            <IpdButton
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={startEditing}
            >
              {assessment ? "Edit" : "Record assessment"}
            </IpdButton>
          )
        }
      >
        {!assessment ? (
          <Box
            sx={{
              textAlign: "center",
              py: 3,
              px: 2,
              border: `1px dashed ${IPD_COLORS.borderHover}`,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
            }}
          >
            <StethoscopeIcon sx={{ fontSize: 26, color: IPD_COLORS.textMuted }} />
            <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700 }}>
              No assessment recorded yet
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
              {disabled
                ? "This visit is closed, and no assessment was recorded on it."
                : "The Emergency Provider records the history, findings, working diagnosis and plan here."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "grid", gap: 1.75 }}>
              {FIELDS.map((f) =>
                assessment[f.key] ? (
                  <Box key={f.key}>
                    <Typography sx={{ ...ipdLabelSx, mb: 0.5 }}>{f.label}</Typography>
                    <Typography sx={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>
                      {assessment[f.key]}
                    </Typography>
                  </Box>
                ) : null
              )}
            </Box>
            {byline}
          </>
        )}
      </IpdSectionCard>
    );
  }

  /* ------------------------------------------------------------ edit mode */
  return (
    <IpdSectionCard
      title="Clinical assessment"
      subtitle={
        assessment
          ? "Corrections are recorded — the visit shows who last changed this"
          : "The first assessment also records the time the patient was seen"
      }
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ display: "grid", gap: 1.75 }}>
        {FIELDS.map((f) => (
          <Box key={f.key}>
            <Typography sx={{ ...ipdLabelSx, mb: 0.75 }}>{f.label}</Typography>
            <IpdTextField
              value={draft[f.key]}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              size="small"
              fullWidth
              multiline
              minRows={f.rows}
              sx={ipdElevatedInputSx}
              inputProps={{ "aria-label": f.label }}
            />
          </Box>
        ))}
      </Box>

      {!anyContent && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textMuted }}>
          Fill in at least one of the four — an assessment with nothing in it is not an
          assessment.
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
        <IpdButton variant="outlined" onClick={cancel} disabled={busy}>
          Cancel
        </IpdButton>
        <IpdButton onClick={submit} loading={busy} disabled={!anyContent}>
          {assessment ? "Save changes" : "Record assessment"}
        </IpdButton>
      </Box>
    </IpdSectionCard>
  );
}
